import { useState } from 'react'
import { useTranslation } from 'libs/i18n'
import { useSearchParams } from 'libs/router'
import { SelectClickTypes } from '@table-library/react-table-library/select'
import {
	ActionsSelect,
	CardHeader,
	CardWrapper,
	CtaButton,
	DeleteConfirmModal,
	EmptyTable,
	Header,
	NameDialog,
	ProductSettingsTabs,
	SearchInput
} from 'shared/components'
import { useDevice, useModal, useSearch, useTableSelect } from 'shared/hooks'
import type { TypeItem } from 'features/types/types/types.types'
import { useActionFilters } from './hooks/useActionFilters'
import { useContextMenuOptions } from './hooks/useContextMenuOptions'
import { useFetchTypes } from './queries/useFetchTypes'
import { useCreateTypeMutation } from './mutations/useCreateTypeMutation'
import { useEditTypeMutation } from './mutations/useEditTypeMutation'
import { useDeleteTypesMutation } from './mutations/useDeleteTypesMutation'
import { useDeleteTypeMutation } from './mutations/useDeleteTypeMutation'
import { TableSection } from './organisms/TableSection'

const TypesPage = () => {
	const { t } = useTranslation()
	const [searchParams] = useSearchParams()
	const { isMobileOrTablet } = useDevice()
	const { handleSearch } = useSearch({ name: 'search' })
	const [checkedTypes, setCheckedTypes] = useState<number[]>([])
	const [idToBeRemoved, setIdToBeRemoved] = useState<string | null>(null)
	const [editedType, setEditedType] = useState<TypeItem | null>(null)
	const { types, isTypesLoading } = useFetchTypes()
	const { select } = useTableSelect({ nodes: types?.data?.results ?? [] }, onSelectChange, {
		clickType: SelectClickTypes.ButtonClick
	})
	const [actionsFilter] = useActionFilters(!checkedTypes.length)

	const {
		isOpen: isAddDialogOpen,
		openModal: openAddDialog,
		closeModal: closeAddDialog
	} = useModal()
	const {
		isOpen: isDeleteModalOpen,
		openModal: openDeleteModal,
		closeModal: closeDeleteModal
	} = useModal()
	const {
		isOpen: isBulkDeleteModalOpen,
		openModal: openBulkDeleteModal,
		closeModal: closeBulkDeleteModal
	} = useModal()

	const { createType, createTypeLoading } = useCreateTypeMutation({
		onSuccess: closeAddDialog
	})

	const { editTypeMutation, isEditTypeLoading } = useEditTypeMutation({
		onSuccess: () => setEditedType(null)
	})

	const { deleteTypeMutation } = useDeleteTypeMutation({
		id: idToBeRemoved,
		onSettled: closeDeleteModal
	})

	const { deleteTypesMutation } = useDeleteTypesMutation({
		ids: checkedTypes,
		onSettled: () => {
			select.fns.onRemoveAll()
			closeBulkDeleteModal()
		}
	})

	function onSelectChange(action: any, state: any) {
		setCheckedTypes(state.ids)
	}

	const handleDelete = (id: number) => {
		setIdToBeRemoved(id.toString())
		openDeleteModal()
	}

	const menuOptions = useContextMenuOptions({ onEdit: setEditedType, onDelete: handleDelete })

	const handleAction = (action: string) => {
		if (action === 'delete') openBulkDeleteModal()
	}

	const renderContent = () => {
		if (isTypesLoading) return <EmptyTable isLoading />
		if (!types?.data?.results?.length)
			return <EmptyTable text={searchParams.toString() ? t('No results') : t('No types yet')} />

		return <TableSection types={types} select={select} menuOptions={menuOptions} />
	}

	return (
		<>
			<Header
				title={t('Products')}
				tabs={<ProductSettingsTabs />}
				actions={
					!isMobileOrTablet && (
						<ActionsSelect options={actionsFilter.options} onChange={handleAction} />
					)
				}
			/>
			<div className="px-5 lg:flex lg:max-h-[calc(100vh-113.5px-4rem)] lg:flex-col lg:px-0">
				<CardWrapper className="lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
					<CardHeader
						title={t('Types')}
						description={t('Product types that can be selected on a product.')}>
						<SearchInput
							variant="filled"
							className="sm:w-64"
							label={t('Search types')}
							placeholder={t('Search types')}
							onChange={handleSearch}
						/>
						<CtaButton text={t('Add type')} onClick={openAddDialog} />
					</CardHeader>
					{renderContent()}
				</CardWrapper>
			</div>

			{isAddDialogOpen && (
				<NameDialog
					title={t('Add type')}
					submitText={t('Create')}
					isLoading={createTypeLoading}
					closeModal={closeAddDialog}
					onSubmit={createType}
				/>
			)}
			{editedType && (
				<NameDialog
					title={t('Edit type')}
					submitText={t('Save')}
					defaultValue={editedType.name}
					isLoading={isEditTypeLoading}
					closeModal={() => setEditedType(null)}
					onSubmit={(data) => editTypeMutation({ id: editedType.id, data })}
				/>
			)}
			{isDeleteModalOpen && (
				<DeleteConfirmModal
					title={t('Delete type')}
					isOpened={isDeleteModalOpen}
					closeModal={closeDeleteModal}
					confirm={deleteTypeMutation}>
					<p>{t('You are about to remove a type. Are you sure you want to proceed?')}</p>
				</DeleteConfirmModal>
			)}
			{isBulkDeleteModalOpen && (
				<DeleteConfirmModal
					title={t('Delete selected types')}
					isOpened={isBulkDeleteModalOpen}
					closeModal={closeBulkDeleteModal}
					confirm={deleteTypesMutation}>
					<p>
						{t('You are about to remove {{ count }} types. Are you sure you want to proceed?', {
							count: checkedTypes.length
						})}
					</p>
				</DeleteConfirmModal>
			)}
		</>
	)
}

export default TypesPage
